import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useCostCurrency } from '../hooks/useCostCurrency'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import ConfirmDialog from '../../schedule/components/ConfirmDialog'
import { useChangeMutations } from '../hooks/useChangeMutations'
import { useContractMutations } from '../hooks/useContractMutations'
import { canCreateApplication } from '../lib/applications'
import { activationBlocker, allocateParamValue, compareMoneyStrings, contractAffordances, contractDetailState } from '../lib/contracts'
import { describeCostControlError, isContractGoneError } from '../lib/errors'
import { moneyLocale } from '../lib/money'
import { canDrawProvisional, drawLine } from '../lib/provisional'
import { buildTree } from '../lib/tree'
import type { CostContract, CostContractDetail, CostContractStatus, CostControlView, CostProvisionalSummary } from '../types'
import RecordAttachmentsCard from '../components/RecordAttachmentsCard'
import RecordHistoryCard from '../components/RecordHistoryCard'
import ContractApplicationsCard from './ContractApplicationsCard'
import ContractChangesCard from './ContractChangesCard'
import ContractDetailSkeleton from './ContractDetailSkeleton'
import ContractDialog from './ContractDialog'
import ContractHeader from './ContractHeader'
import ContractRepresentativesCard from './ContractRepresentativesCard'
import ContractSubmittalsCard from './ContractSubmittalsCard'
import ContractInvoicesCard from './ContractInvoicesCard'
import ContractLinesCard from './ContractLinesCard'
import ContractPriceCard from './ContractPriceCard'
import ContractProvisionalCard from './ContractProvisionalCard'
import DrawProvisionalDialog from './DrawProvisionalDialog'
import ContractRetentionCard from './ContractRetentionCard'
import ContractLinesDialog from './ContractLinesDialog'
import PriceItemsDialog from './PriceItemsDialog'
import ContractSummaryCards from './ContractSummaryCards'

type Pending = { kind: 'status'; to: CostContractStatus } | { kind: 'delete' } | { kind: 'import' } | null

const ContractDetail: React.FC = () => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { match, history, location } = useRouter()
  const { projectId, contractId } = match.params as { projectId: string; contractId: string }
  const actions = useModuleActions('COST_CONTROL', projectId)
  const procurement = useModuleActions('PROCUREMENT', projectId)
  const docControl = useModuleActions('DOC_CONTROL', projectId)
  const submittalsActions = useModuleActions('SUBMITTALS', projectId)
  const { data, loading, error } = useQuery<{ costContract: CostContractDetail }>(Query.COST_CONTRACT, {
    variables: { id: contractId },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  // The budget view gives the item tree for the lines editor (Task 9).
  // cache-and-network (3a deferred item): a budget edited meanwhile shows in the lines editor.
  const { data: viewData } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    skip: !actions.canView,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const currency = useCostCurrency(projectId)
  const { data: contractData } = useQuery<{ costContracts: CostContract[] }>(Query.COST_CONTRACTS, {
    variables: { projectId, filter: {} },
    skip: !actions.canView,
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  // A write's reload of this contract can be refused (someone deleted it); the page's own query never hears of it.
  const [lost, setLost] = useState<{ id: string; message: string } | null>(null)
  const onDetailError = useCallback((id: string, e: unknown) => {
    const message = (e as { message?: string } | null)?.message ?? ''
    if (isContractGoneError(message)) setLost({ id, message })
  }, [])
  const mutations = useContractMutations(projectId, onDetailError)
  const changeMutations = useChangeMutations(projectId)
  const [drawing, setDrawing] = useState<CostProvisionalSummary | null>(null)
  const [editing, setEditing] = useState(false)
  const [pending, setPending] = useState<Pending>(null)
  const [linesOpen, setLinesOpen] = useState(false)
  const [initialAllocate, setInitialAllocate] = useState<string | null>(null)
  const tree = useMemo(() => buildTree(viewData?.costControl.nodes ?? []), [viewData])
  const allocateParam = allocateParamValue(new URLSearchParams(location.search).get('allocate') ?? undefined)
  const detail = data?.costContract
  const isRep = detail?.access === 'REPRESENTATIVE'
  const message = (lost && lost.id === contractId ? lost.message : null) ?? error?.message ?? null
  const state = contractDetailState({
    loading, hasData: !!detail, contractProjectId: detail?.contract.projectId ?? null, projectId, message, rightsLoading: actions.loading, canView: actions.canView,
  })
  const priceItemCount = detail?.priceItems.length ?? 0
  const af = contractAffordances(actions, procurement.canView, state === 'ready' && detail ? detail.contract : null, priceItemCount)
  const [priceOpen, setPriceOpen] = useState(false)
  useEffect(() => { if (!af.editPriceItems) setPriceOpen(false) }, [af.editPriceItems])
  const canEditLines = af.editLines
  // A dialog closes as soon as its action is no longer allowed: a refused save that changed the contract (3a deferred item).
  useEffect(() => { if (!af.editHeader) setEditing(false) }, [af.editHeader])
  useEffect(() => { if (!af.editLines) setLinesOpen(false) }, [af.editLines])
  // "Z tendru" hands the awarded price over in ?allocate=: open the editor once, with the pro-rata dialog on top.
  useEffect(() => {
    if (!allocateParam || !canEditLines || !viewData) return
    setInitialAllocate(allocateParam)
    setLinesOpen(true)
    history.replace(`/project/${projectId}/cost-control/contracts/${contractId}`)
  }, [allocateParam, canEditLines, viewData, history, projectId, contractId])
  const back = () => history.push(isRep ? `/project/${projectId}/cost-control` : `/project/${projectId}/cost-control?tab=contracts`)

  if (state === 'loading') return <ContractDetailSkeleton />
  if (state !== 'ready' || !detail) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" onClick={back}>
          <ArrowLeft className="h-4 w-4" />
          {t('CostControl_contracts_back')}
        </Button>
        <p className="text-sm text-muted-foreground">{state === 'error' ? describeCostControlError(message, t) : t('CostControl_error_contract_not_found')}</p>
      </div>
    )
  }

  const { contract } = detail
  const confirmText = !pending ? null
    : pending.kind === 'import' ? { title: 'CostControl_price_import_title', text: detail.lines.length ? 'CostControl_price_import_text_replaces' : 'CostControl_price_import_text', label: 'CostControl_price_import' }
    : pending.kind === 'delete' ? { title: 'CostControl_contract_delete_title', text: 'CostControl_contract_delete_text', label: 'Common_delete' }
      : pending.to === 'CLOSED' ? { title: 'CostControl_contract_close_title', text: 'CostControl_contract_close_text', label: 'CostControl_contract_close' }
        : contract.status === 'CLOSED' ? { title: 'CostControl_contract_reopen_title', text: 'CostControl_contract_reopen_text', label: 'CostControl_contract_reopen' }
          : { title: 'CostControl_contract_activate_title', text: contract.kind === 'MAIN' ? 'CostControl_contract_activate_text_main' : priceItemCount > 0 ? 'CostControl_contract_activate_text_items' : 'CostControl_contract_activate_text', label: 'CostControl_contract_activate' }

  const confirm = async () => {
    if (!pending) return
    if (pending.kind === 'import') {
      await mutations.importPriceItems(contract.id)
      setPending(null)
      return
    }
    if (pending.kind === 'delete') {
      const res = await mutations.remove(contract.id)
      setPending(null)
      if (res.ok) {
        toastr.success('', t('CostControl_contract_deleted'))
        back()
      }
      return
    }
    await mutations.setStatus(contract.id, pending.to, contract.status)
    setPending(null)
  }

  // Part D spec §2.4, no new mutation: a draft contract change, then its one negative line; the page opens the change,
  // where the positive lines on the real items are added. A failed second call leaves an empty draft, which is harmless.
  const draw = async (title: string, amount: string) => {
    if (!drawing) return false
    const res = await changeMutations.create({ kind: 'CONTRACT_CHANGE', title, reason: null, contractId: contract.id })
    if (!res.ok) return false
    const changeId = res.data.createCostChange.id
    await changeMutations.setLines(changeId, [drawLine(drawing.nodeId, amount)])
    history.push(`/project/${projectId}/cost-control/changes/${changeId}`)
    return true
  }

  return (
    <div className="space-y-4">
      <ContractHeader
        contract={contract}
        af={af}
        blocker={activationBlocker(contract, detail.priceTotals.unmappedCount)}
        onBack={back}
        onEdit={() => setEditing(true)}
        onStatus={(to) => setPending({ kind: 'status', to })}
        onDelete={() => setPending({ kind: 'delete' })}
      />
      <ContractSummaryCards contract={contract} projectId={projectId} currency={currency} locale={locale} canOpenTender={procurement.canView} />
      {contract.kind === 'SUB' && (
        <ContractPriceCard
          items={detail.priceItems}
          totals={detail.priceTotals}
          tree={tree}
          currency={currency}
          locale={locale}
          canEdit={af.editPriceItems}
          canImport={af.importPriceItems}
          showBudgetItem={!isRep}
          onEdit={() => setPriceOpen(true)}
          onImport={() => setPending({ kind: 'import' })}
        />
      )}
      {!isRep && (
        <ContractLinesCard contract={contract} lines={detail.lines} currency={currency} locale={locale} canEdit={af.editLines} derived={priceItemCount > 0} onEdit={() => { setInitialAllocate(null); setLinesOpen(true) }} />
      )}
      {!isRep && detail.provisional.length > 0 && (
        <ContractProvisionalCard rows={detail.provisional} currency={currency} locale={locale} canDraw={(r) => canDrawProvisional(actions, contract, r)} onDraw={setDrawing} />
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <ContractApplicationsCard contract={contract} applications={detail.applications} projectId={projectId} currency={currency} locale={locale} canCreate={canCreateApplication(actions, contract, detail.access)} />
        {(detail.retention.percent !== null || detail.retention.releases.length > 0 || compareMoneyStrings(detail.retention.retained, '0') > 0) && (
          <ContractRetentionCard
            retention={detail.retention}
            currency={currency}
            locale={locale}
            canRelease={actions.canApprove}
            saving={mutations.saving}
            onAdd={async (input) => (await mutations.addRelease(contract.id, input)).ok}
            onDelete={async (id) => (await mutations.deleteRelease(contract.id, id)).ok}
          />
        )}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <ContractChangesCard contract={contract} contracts={contractData?.costContracts ?? []} projectId={projectId} currency={currency} locale={locale} canCreate={actions.canCreate} />
        {contract.kind === 'SUB' && (
          <ContractInvoicesCard contract={contract} contracts={contractData?.costContracts ?? []} projectId={projectId} currency={currency} locale={locale} canCreate={actions.canCreate} />
        )}
      </div>
      {contract.kind === 'SUB' && (
        <div className="grid gap-4 lg:grid-cols-2">
          {!isRep && (
            <ContractRepresentativesCard
              projectId={projectId}
              representatives={detail.representatives}
              canEdit={actions.canEdit}
              saving={mutations.saving}
              onAdd={async (userId) => (await mutations.addRepresentative(contract.id, userId)).ok}
              onRemove={async (id) => (await mutations.removeRepresentative(contract.id, id)).ok}
            />
          )}
          <ContractSubmittalsCard
            projectId={projectId}
            contractId={contract.id}
            submittals={detail.submittals}
            canEdit={!isRep && actions.canEdit && submittalsActions.canView && contract.status !== 'CLOSED'}
            onLink={async (ids) => (await mutations.linkSubmittals(contract.id, ids)).ok}
            onUnlink={async (id) => (await mutations.unlinkSubmittal(contract.id, id)).ok}
          />
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <RecordAttachmentsCard
          projectId={projectId}
          attachments={detail.attachments}
          canAdd={af.addAttachment}
          canRemove={af.removeAttachment}
          canLinkDocuments={docControl.canView}
          onAdd={(source) => mutations.addAttachment(contract.id, source)}
          onRemove={(attachmentId) => mutations.removeAttachment(contract.id, attachmentId)}
        />
        {!isRep && <RecordHistoryCard projectId={projectId} entityType="CONTRACT" entityId={contract.id} currency={currency} locale={locale} />}
      </div>
      <ContractDialog
        open={editing}
        mode="edit"
        contract={contract}
        currency={currency}
        allowMain={false}
        onOpenChange={setEditing}
        onSubmit={async (input) => (await mutations.update(contract.id, input)).ok}
      />
      <ContractLinesDialog
        open={linesOpen}
        contract={contract}
        lines={detail.lines}
        tree={tree}
        projectId={projectId}
        currency={currency}
        locale={locale}
        initialAllocate={initialAllocate}
        onOpenChange={(open) => { setLinesOpen(open); if (!open) setInitialAllocate(null) }}
        onSave={async (lines) => (await mutations.setLines(contract.id, lines)).ok}
      />
      <PriceItemsDialog
        open={priceOpen}
        contractId={contract.id}
        projectId={projectId}
        items={detail.priceItems}
        discountPercent={detail.discountPercent}
        discountAmount={detail.discountAmount}
        tree={tree}
        currency={currency}
        locale={locale}
        replacesLines={detail.lines.length > 0 && detail.priceItems.length === 0}
        onOpenChange={setPriceOpen}
        onSave={async (items, discount) => (await mutations.setPriceItems(contract.id, items, discount)).ok}
      />
      <DrawProvisionalDialog row={drawing} currency={currency} locale={locale} onClose={() => setDrawing(null)} onSubmit={draw} />
      <ConfirmDialog
        open={!!pending}
        title={confirmText ? t(confirmText.title) : ''}
        description={confirmText ? t(confirmText.text) : undefined}
        confirmLabel={confirmText ? t(confirmText.label) : ''}
        variant={pending?.kind === 'delete' ? 'destructive' : 'default'}
        submitting={mutations.saving}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      />
    </div>
  )
}
export default ContractDetail
